/* global window */

import log from 'loglevel';
import get from 'lodash/get';
import set from 'lodash/set';
import find from 'lodash/find';
import map from 'lodash/map';
import isMobileJS from 'ismobilejs';
import UAParser from 'ua-parser-js';
import axios from 'axios';
import Cookies from 'js-cookie';
import geolocator from 'geolocator';
import store from 'store';

import settings from './settings';
import liid from './liid';
import { getPublishedMessageFormat } from './message';

/**
 * Class representing PIP.
 */
class PIP {
  /**
   * Create a PIP instance.
   * @param {Object} params - PIP configuration parameters.
   */
  constructor(parms) {
    this.parms = parms;
    this.globalConfig = null;
    this.location = null;
    this.neighborhoods = null;
    this.lastLocationRequestKey = 'MetaphorLastLocationRequest';
    this.lastPIPRequestKey = 'MetaphorLastPIPRequest';
    this.userLocationPermissionGranted = false;
  }

  /**
   * Check if PIP is ready.
   * @returns {Boolean}
   */
  get isPIPReady() {
    return this.globalConfig !== null;
  }

  /**
   * Load PIP global config file.
   * @returns {Promise}
   */
  load() {
    let pipUrl = get(settings, 'globalConfigUrl');
    if (!pipUrl) {
      log.debug('Invalid PIP settings.');
      return Promise.resolve();
    }

    pipUrl = pipUrl.replace('{env}', this.env);

    return axios.get(pipUrl, {
      timeout: settings.timeout,
    })
      .then(response => {
        this.globalConfig = get(response, 'data', null);

        if (this.topics.length > 0) {
          const allTopics = get(this.globalConfig, 'topic_manager.topics', {});
          const filteredTopics = allTopics.filter(topic => topic.enabled);
          set(this.globalConfig, 'topic_manager.topics', filteredTopics);
        }
      })
      .catch(error => log.error(error))
      .then(() => Promise.resolve());
  }

  /**
   * Initialize PIP.
   */
  init() {
    log.debug('PIP initialized.');
  }

  /**
   * Return site env.
   * @returns {String}
   */
  get env() {
    return get(settings, 'env', 'dev');
  }

  /**
   * Return site ID.
   * @returns {String}
   */
  get siteId() {
    return get(settings, 'site_id', 'vl_nola');
  }

  /**
   * Return topic manager version.
   * @returns {String}
   */
  get version() {
    return get(this.globalConfig, 'topic_manager.version', '');
  }

  /**
   * Return a list of available topics.
   * @returns {Array}
   */
  get topics() {
    const topicManager = get(this.globalConfig, 'topic_manager.topics', {});
    const topics = map(topicManager, 'name');
    return topics;
  }

  /**
   * Return Hoodie URL.
   * @returns {String}
   */
  get hoodieUrl() {
    return get(this.globalConfig, 'hoodie_url', '');
  }

  /**
   * Retrieves API key for a topic.
   * @param {String} topicName - topic name.
   * @returns {String}
   */
  getApiKey(topicName) {
    const topicManager = get(this.globalConfig, 'topic_manager.topics', {});
    const topicData = find(topicManager, {
      name: topicName,
    });
    return get(topicData, 'apikey', '');
  }

  /**
   * Retrieves hash difficulty for a topic.
   * @param {String} topicName - topic name.
   * @returns {String}
   */
  getDifficulty(topicName) {
    const topicManager = get(this.globalConfig, 'topic_manager.topics', {});
    const topicData = find(topicManager, {
      name: topicName,
    });
    return get(topicData, 'powd', 3);
  }

  /**
   * Retrieves endpoint for a topic.
   * @param {String} topicName - topic name.
   * @param {Boolean} rawFormat - if true, returns endpoint without replacing macros.
   * @returns {String}
   */
  getEndpoint(topicName, rawFormat = false) {
    const topicManager = get(this.globalConfig, 'topic_manager.topics', {});
    const topicData = find(topicManager, {
      name: topicName,
    });

    let endpoint = get(topicData, 'endpoint', '');
    if (rawFormat) {
      return endpoint;
    }

    endpoint = endpoint.replace('{env}', this.env);
    endpoint = endpoint.replace('{version}', this.version);

    return endpoint;
  }

  /**
   * Format user linking IDs array.
   * @param {String} sailthruId - SHA-256 hash of user's email.
   * @param {String} brazeId - Braze user ID.
   * @returns {Array}
   */
  getUserLinkingIds(sailthruId, brazeId) {
    const ids = [];

    if (sailthruId) {
      ids.push({
        user_id: sailthruId,
        user_id_context: 'sha256-email',
      });

      ids.push({
        user_id: sailthruId,
        user_id_context: 'sailthruID',
      });
    }

    if (brazeId) {
      ids.push({
        user_id: brazeId,
        user_id_context: 'brazeID',
      });
    }

    let anonUserCookie = null;
    if (!ids.length) {
      anonUserCookie = Cookies.get('sailthru_visitor');
    }
    if (anonUserCookie) {
      ids.push({
        user_id: anonUserCookie,
        user_id_context: 'sailthruID-anon',
      });
    }

    const idsDebug = map(ids, 'user_id_context');
    log.debug(`User linking IDs = [${idsDebug.join(', ')}]`);

    return ids;
  }

  /**
   * Formats a user linking message payload.
   * @param {Array} ids - Array of user IDs.
   * @returns {Object}
   */
  getUserLinkingPayload(ids, ts = Date.now()) {
    const parser = new UAParser();
    const result = parser.getResult();

    const platform = `${get(result, 'os.name')} ${isMobileJS.any ? 'mobile' : 'desktop'}`;

    return {
      version: '1.0',
      ts,
      liid: liid(),
      device: get(result, 'device.model', null),
      user_agent: get(result, 'browser.name', null),
      client_version: get(result, 'browser.version', null),
      platform,
      site_id: this.siteId,
      ids,
    };
  }

  /**
   * Send user linking data to PIP.
   * @param {String} sailthruId - SHA-256 hash of user's email.
   * @param {String} brazeId - Braze user ID.
   * @returns {Promise}
   */
  sendUserLinkingData(sailthruId, brazeId) {
    if (!this.isPIPReady) {
      log.error('Tried to send PIP data before config was loaded.');
      return Promise.resolve();
    }

    const topic = 'userid';
    const ids = this.getUserLinkingIds(sailthruId, brazeId);

    if (!ids.length) {
      log.error('No user linking IDs found.');
      return Promise.resolve();
    }

    const message = getPublishedMessageFormat(
      JSON.stringify(this.getUserLinkingPayload(ids)),
      this.getApiKey(topic),
      topic,
      this.getDifficulty(topic),
    );

    let postSuccess = false;
    log.debug('Sending user linking data to PIP.');

    return axios.post(this.getEndpoint(topic), message, {
      headers: {
        'Content-Type': 'text/plain',
      },
    })
      .then(() => {
        postSuccess = true;
      })
      .catch(error => log.error(error))
      .then(() => {
        if (postSuccess) {
          log.debug('User linking data sent to PIP successfully.');
        }
        return Promise.resolve();
      });
  }

  /**
   * Fetch neighborhoods from user location data.
   * @returns {Promise}
   */
  getUserNeighborhoods() {
    if (!this.location) {
      return Promise.resolve();
    }

    const { lat, lon } = this.location[0];
    const requestUrl = `${this.hoodieUrl}?lat=${lat}&lon=${lon}`;

    return axios.get(requestUrl, {
      timeout: settings.timeout,
    })
      .then(response => {
        this.neighborhoods = get(response, 'data.neighborhoods', null);
      })
      .catch(error => log.error(error))
      .then(() => Promise.resolve());
  }

  /**
   * Check if user geo location permission has been granted.
   * @returns {Promise}
   */
  getUserLocationPermissionStatus() {
    if (!get(window, 'navigator.permissions')) {
      return Promise.resolve();
    }

    const permission = {
      name: 'geolocation',
    };

    return new Promise(resolve => {
      window.navigator.permissions.query(permission)
        .then(result => {
          this.userLocationPermissionGranted = result.state === 'granted';
          return resolve();
        });
    });
  }

  /**
   * Check if user location prompt can be triggered.
   * @returns {Boolean}
   */
  get canRequestUserLocation() {
    if (this.userLocationPermissionGranted) {
      return true;
    }

    if (!store.enabled) {
      log.error('Storage not available.');
      return true;
    }

    const lastRequest = store.get(this.lastLocationRequestKey);
    if (typeof lastRequest === 'undefined') {
      return true;
    }

    if (Date.now() - lastRequest > settings.sessionLength) {
      return true;
    }

    return false;
  }

  /**
   * Check if user location data can be sent to PIP.
   * @returns {Boolean}
   */
  get canSendUserLocationToPIP() {
    if (!store.enabled) {
      log.error('Storage not available.');
      return true;
    }

    const sendThreshold = 1000;
    const lastRequest = store.get(this.lastPIPRequestKey);
    if (typeof lastRequest === 'undefined') {
      return true;
    }

    if (Date.now() - lastRequest > sendThreshold) {
      return true;
    }

    return false;
  }

  /**
   * Set user location data.
   * @param {String} latitude - Latitude.
   * @param {String} longitude - Longitude.
   */
  setUserLocation(latitude, longitude) {
    this.location = [
      {
        ts: Date.now(),
        lat: latitude,
        lon: longitude,
        motion_recognition: [
          {
            motion_type: 'still',
            motion_confidence_score: 0,
          },
        ],
      },
    ];
  }

  /**
   * Ask for permission and fetch user geo location.
   * @param {Boolean} ignoreSession - Ignore session location prompt limit.
   * @returns {Promise}
   */
  getUserLocation(ignoreSession = false) {
    if (!ignoreSession && !this.canRequestUserLocation) {
      return Promise.resolve();
    }

    const options = {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumWait: 10000,
      maximumAge: 0,
      fallbackToIP: false,
      addressLookup: false,
      timezone: false,
    };

    return new Promise(resolve => {
      geolocator.locate(options, (err, location) => {
        if (!err) {
          this.setUserLocation(location.coords.latitude, location.coords.longitude);
        }

        store.set(this.lastLocationRequestKey, Date.now());

        resolve();
      });
    });
  }

  /**
   * Formats a user location message payload.
   * @param {Object} location - User location object.
   * @returns {Object}
   */
  getUserLocationPayload(location, ts = Date.now()) {
    const parser = new UAParser();
    const result = parser.getResult();

    const platform = `${get(result, 'os.name')} ${isMobileJS.any ? 'mobile' : 'desktop'}`;

    return {
      version: '1.0',
      ts,
      user_id: liid(),
      user_id_context: 'liid',
      device: get(result, 'device.model', null),
      user_agent: get(result, 'browser.name', null),
      client_version: get(result, 'browser.version', null),
      platform,
      site_id: this.siteId,
      location,
    };
  }

  /**
   * Send user location data to PIP.
   * @returns {Promise}
   */
  sendUserLocationData() {
    if (!this.isPIPReady) {
      log.error('Tried to send PIP data before config was loaded.');
      return Promise.resolve();
    }

    if (!this.canSendUserLocationToPIP) {
      return Promise.resolve();
    }

    if (!this.location) {
      log.error('No user location data found.');
      return Promise.resolve();
    }

    store.set(this.lastPIPRequestKey, Date.now());
    const topic = 'userloc';

    const message = getPublishedMessageFormat(
      JSON.stringify(this.getUserLocationPayload(this.location)),
      this.getApiKey(topic),
      topic,
      this.getDifficulty(topic),
    );

    let postSuccess = false;
    log.debug('Sending user location data to PIP.');

    return axios.post(this.getEndpoint(topic), message, {
      headers: {
        'Content-Type': 'text/plain',
      },
    })
      .then(() => {
        postSuccess = true;
      })
      .catch(error => log.error(error))
      .then(() => {
        if (postSuccess) {
          log.debug('User location data sent to PIP successfully.');
        }
        return Promise.resolve();
      });
  }
}

export default PIP;

window.Metaphor = window.Metaphor || {};
window.Metaphor.PIP = PIP;



// WEBPACK FOOTER //
// ./src/PIP.js